import { useContext, useState } from 'react';

import { useStorePrice } from '@/common/hooks/apple-price';
import { APPLE_VIP, isAppleIapAvailable } from '@/common/lib/apple-iap';
import { ModalContext } from '@/components/Modals/Modal/config';
import { selectAvailableVipPaidOffers } from '@/redux/selectors/userSelectors';
import { useAppSelector } from '@/redux/store';

interface VipPlan {
  days: number;
  discountPrice: number;
  starPrice: number;
}

interface VipPlans {
  plans: VipPlan[];
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  selected: VipPlan | undefined;
  /** Discount in percent shown on a plan (none in the App Store build) */
  sales: Record<number, number>;
  /** The "before" price of a plan, crossed out next to its price */
  getPriceByDiscount: (index: number) => number;
  /** App Store price of the selected plan, null outside the iOS app */
  storePrice: string | null;
  /** Opens the payment window of the selected plan */
  buy: () => void;
  isAppStore: boolean;
}

/** VIP periods for sale and buying one — shared by the VIP tab and modal. */
const useVipPlans = (initialIndex = 0): VipPlans => {
  const { openModal } = useContext(ModalContext);
  const telegramOffers = useAppSelector(selectAvailableVipPaidOffers) ?? [];
  const isAppStore = isAppleIapAvailable();
  // The App Store has its own VIP prices (every period is a subscription)
  const plans: VipPlan[] = isAppStore
    ? Object.entries(APPLE_VIP).map(([days, { price }]) => ({
        days: Number(days),
        discountPrice: price,
        starPrice: 0,
      }))
    : telegramOffers;
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);
  const selected = plans[selectedIndex] ?? plans[0];
  const storePrice = useStorePrice(
    undefined,
    isAppStore ? selected?.days : undefined,
  );

  // Promo "discounts" against a made-up original price stay out of the
  // App Store build (misleading pricing is a review rejection).
  const sales: Record<number, number> = isAppStore
    ? {}
    : {
        // Savings against the weekly price per day
        1: 30,
        2: 40,
        3: 50,
      };

  const getPriceByDiscount = (i: number): number => {
    const { discountPrice } = plans[i];
    const sale = sales[i];

    if (!sale) return discountPrice;

    return Number((discountPrice / ((100 - sale) / 100)).toFixed(2));
  };

  const buy = (): void => {
    if (!selected) return;

    openModal({
      renderKind: 'buyVip',
      props: {
        days: selected.days,
        price: selected.discountPrice,
        starPrice: selected.starPrice,
        discount: sales[plans.indexOf(selected)],
      },
    });
  };

  return {
    plans,
    selectedIndex: selected ? plans.indexOf(selected) : 0,
    setSelectedIndex,
    selected,
    sales,
    getPriceByDiscount,
    storePrice,
    buy,
    isAppStore,
  };
};

export { useVipPlans, type VipPlan };
